import { useState, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import rehypeSanitize from "rehype-sanitize";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import Button from "src/components/Button";
import Loading from "src/components/Loading";
import PeoplePicker from "src/components/ui/form/peoplePicker";
import Datepicker from "react-tailwindcss-datepicker";
import { twMerge } from "tailwind-merge";
import { useAppSelector } from "src/hooks/redux";
import { selectCurrentUser } from "src/modules/users/selector";
import Comments from "src/modules/comments";
import {
    myTask5WayCommentsFilesListName,
    myTasks5WayCommentsListName,
} from "src/api/config/myTasks5Way.config";
import { MT5WhyCommentsFiles } from "src/api/config";
import {
    fetchRateQuestions,
    fetchRateSettingsDescription,
    fetchExistingRate,
    fetchCardDocumentId,
    fetchUserDocumentId,
    fetchRatingColorThreshold,
    saveRate,
    DEFAULT_RATING_COLOR_THRESHOLD,
    type RateQuestion,
    type RateAnswerPayload,
    type RatingColorThreshold,
} from "../../api/rate5Why.api";
import { getRatingTextColor } from "../RatingBadge/RatingBadge";
import remarkGfm from "remark-gfm";

type AnswerValue = "yes" | "no" | null;

const formatWeight = (value: number): string => {
    if (value === 0) return "0%";
    const rounded = Math.round(value * 10) / 10;
    return `${rounded.toString().replace(".", ",")}%`;
};

type Props = {
    onClose: () => void;
    onSuccess: (rateId: number, rateDocumentId: string, totalWeight: number) => void;
    card5WayID: number;
    existingRateId: number | null;
    existingRateDocumentId: string | null;
    viewMode: boolean;
    cardType: string;
    zIndex: number;
};

const Rate5Why = ({
    onClose,
    onSuccess,
    card5WayID,
    existingRateId,
    existingRateDocumentId,
    viewMode,
    cardType,
    zIndex,
}: Props) => {
    const currentUser = useAppSelector((state) => selectCurrentUser(state));

    const [questions, setQuestions] = useState<RateQuestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [answers, setAnswers] = useState<Record<number, AnswerValue>>({});
    const [evaluator, setEvaluator] = useState<any[]>(() =>
        currentUser
            ? [{ value: currentUser.email, label: currentUser.displayName, avatar: currentUser.avatar, email: currentUser.email, id: currentUser.id }]
            : [],
    );

    const todayDate = new Date();
    const minAllowedDate = new Date(todayDate);
    minAllowedDate.setDate(todayDate.getDate() - 7);
    const today = todayDate.toISOString().split("T")[0];
    const [evaluationDate, setEvaluationDate] = useState<any>({ startDate: today, endDate: today });
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [colorThresholds, setColorThresholds] = useState<RatingColorThreshold>(DEFAULT_RATING_COLOR_THRESHOLD);
    const [pageDescription, setPageDescription] = useState("");

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [items, rate, thresholds, description] = await Promise.all([
                    fetchRateQuestions(cardType),
                    existingRateDocumentId ? fetchExistingRate(existingRateDocumentId) : Promise.resolve(null),
                    fetchRatingColorThreshold(),
                    fetchRateSettingsDescription(cardType),
                ]);

                setQuestions(items);
                setColorThresholds(thresholds);
                setPageDescription(description);

                if (rate) {
                    if (rate.RateDate) {
                        setEvaluationDate({ startDate: rate.RateDate, endDate: rate.RateDate });
                    }
                    if (rate.RateUser) {
                        const rateUserName = rate.RateUser.Name ?? rate.RateUser.username ?? "";
                        const rateUserPersNumber = rate.RateUser.PersonalNumber ?? "";
                        setEvaluator([{
                            value: rate.RateUser.email ?? "",
                            label: rateUserPersNumber ? `${rateUserName} (${rateUserPersNumber})` : rateUserName,
                            email: rate.RateUser.email ?? "",
                            id: rate.RateUser.id,
                            avatar: rate.RateUser.PictureUrl?.url ?? null,
                        }]);
                    }
                    const rawAnswers = cardType === "DeviationReport"
                        ? (rate.AnswersDeviation ?? rate.Answers)
                        : rate.Answers;
                    if (Array.isArray(rawAnswers)) {
                        const initialAnswers: Record<number, AnswerValue> = {};
                        for (const a of rawAnswers) {
                            const qId = a.Question?.id ?? (a.Question as any);
                            if (qId) {
                                initialAnswers[qId] = a.Answer === "Да" ? "yes" : "no";
                            }
                        }
                        setAnswers(initialAnswers);
                    }
                }
            } catch {
                setQuestions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [existingRateDocumentId, cardType]);

    const visibleQuestions = useMemo(
        () => questions.filter((q) => !q.IsHidden),
        [questions],
    );

    const groupedQuestions = useMemo(() => {
        const map: Record<string, RateQuestion[]> = {};
        for (const q of visibleQuestions) {
            if (!map[q.QuestionBlock]) map[q.QuestionBlock] = [];
            map[q.QuestionBlock].push(q);
        }
        return map;
    }, [visibleQuestions]);

    const orderedBlocks = useMemo(() => {
        const blockNames = Object.keys(groupedQuestions);
        if (!blockNames.length) return [];

        return [...blockNames].sort((a, b) => {
            const aFirst = groupedQuestions[a]?.[0];
            const bFirst = groupedQuestions[b]?.[0];
            const sortDiff = (aFirst?.QuestionBlockSortOrder ?? 0) - (bFirst?.QuestionBlockSortOrder ?? 0);
            if (sortDiff !== 0) return sortDiff;

            return a.localeCompare(b, "ru");
        });
    }, [groupedQuestions]);

    const totalScore = useMemo(() => {
        let sum = 0;
        for (const [idStr, answer] of Object.entries(answers)) {
            const qId = Number(idStr);
            const question = visibleQuestions.find((q) => q.id === qId);
            if (!question || !answer) continue;
            sum += answer === "yes" ? question.WeightYes : question.WeightNo;
        }
        return Math.round(sum * 10) / 10;
    }, [answers, visibleQuestions]);

    const handleAnswerChange = (questionId: number, value: AnswerValue) => {
        if (viewMode) return;
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    };

    const handleSubmit = async () => {
        const newErrors: Record<string, boolean> = {};
        if (!evaluator.length) newErrors.evaluator = true;
        if (!evaluationDate?.startDate) newErrors.evaluationDate = true;
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        setSubmitting(true);
        try {
            const [cardDocumentId, userDocumentId] = await Promise.all([
                fetchCardDocumentId(card5WayID),
                fetchUserDocumentId(evaluator[0]?.id),
            ]);

            const answersPayload: RateAnswerPayload[] = visibleQuestions
                .filter((q) => answers[q.id] != null)
                .map((q) => ({
                    Question: { connect: [{ documentId: q.documentId ?? q.id }] },
                    Answer: answers[q.id] === "yes" ? "Да" : "Нет",
                }));

            const result = await saveRate({
                cardDocumentId,
                userDocumentId,
                rateDate: evaluationDate.startDate,
                totalWeight: totalScore,
                answers: answersPayload,
                isDeviationReport: cardType === "DeviationReport",
                existingRateDocumentId,
            });

            if (!result) return;

            const savedId = result.id ?? existingRateId ?? 0;
            const savedDocumentId = result.documentId ?? existingRateDocumentId ?? "";
            onSuccess(savedId, savedDocumentId, totalScore);
            onClose();
        } catch {
            console.error("Error saving rate");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center w-full h-full mt-10">
                <Loading />
            </div>
        );
    }
    
    const pageTitle = cardType === "DeviationReport"
        ? "Чек-лист оценки Отчета об отклонении"
        : "Чек-лист оценки 5Почему";
    const submitButtonText = cardType === "DeviationReport" ? "Оценить Отчет об отклонении" : "Оценить 5Почему";

    return (
        <FlexCol className="h-full overflow-y-auto">
            <div className="flex flex-col p-10 shrink gap-0 pb-6">
                <FlexRow className="justify-between items-start gap-5 pb-8">
                    <FlexCol className="gap-2 flex-1 min-w-0">
                        <h1 className="text-black text-[30px] font-bold leading-[38px]">{pageTitle}</h1>
                        {pageDescription && (
                            <div className="markdown-text text-[#637381] text-base font-normal leading-[22px] w-full">
                                <ReactMarkdown remarkPlugins={[remarkBreaks, remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                                    {pageDescription}
                                </ReactMarkdown>
                            </div>
                        )}
                    </FlexCol>
                    <span className="text-lg font-medium leading-6 text-[#111928] shrink-0">
                        <span className={getRatingTextColor(totalScore, colorThresholds)}>
                            {formatWeight(totalScore)}
                        </span>
                        {" / 100%"}
                    </span>
                </FlexRow>

                <FlexRow className="gap-5 pb-[.5rem] items-start">
                    <div className="flex-1 min-w-0">
                        <PeoplePicker
                            dataField={{
                                type: "peoplePicker",
                                key: "Evaluator",
                                title: "ФИО оценщика",
                                require: true,
                                width: "w-full",
                                placeholder: "Начните вводить имя",
                                single: true,
                                disabled: viewMode,
                            }}
                            onChange={(data: any) => {
                                if (!viewMode) setEvaluator(data);
                            }}
                            value={evaluator}
                            errors={errors.evaluator ? { Evaluator: true } : {}}
                            type="peoplePicker"
                        />
                        {errors.evaluator && (
                            <p className="text-[#BA0C2F] text-sm mt-1">Заполните поле</p>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <FlexRow className="gap-1 mb-2 text-base leading-[22px]">
                            <span className="flex-1 text-[#111928] font-normal">Дата оценки</span>
                            {!viewMode && <span className="text-[#BA0C2F] font-medium">*</span>}
                        </FlexRow>
                        <Datepicker
                            inputClassName={twMerge(
                                "text-left cursor-default block w-full text-base rounded-md border py-3 px-5 text-black outline-none",
                                errors.evaluationDate ? "border-[#BA0C2F]" : "border-[#DFE4EA]",
                                viewMode && "bg-[#F9FAFB] text-[#6B7280] cursor-not-allowed",
                            )}
                            inputId="5why-rate-datepicker"
                            inputName="5why-rate-datepicker"
                            useRange={false}
                            toggleClassName={twMerge(
                                "absolute right-[8px] top-0 h-full px-3 text-gray-400 focus:outline-none",
                                viewMode && "pointer-events-none",
                            )}
                            asSingle={true}
                            i18n="ru"
                            startWeekOn="mon"
                            primaryColor="blue"
                            displayFormat="DD.MM.YYYY"
                            readOnly={true}
                            disabled={viewMode}
                            minDate={minAllowedDate}
                            maxDate={todayDate}
                            value={evaluationDate}
                            onChange={(val: any) => {
                                if (viewMode) return;
                                setEvaluationDate(val);
                                setErrors((prev) => ({ ...prev, evaluationDate: false }));
                            }}
                        />
                        {errors.evaluationDate && (
                            <p className="text-[#BA0C2F] text-sm mt-1">Заполните поле</p>
                        )}
                    </div>
                </FlexRow>

                <FlexCol className="gap-5 pb-10">
                    {orderedBlocks.map((block) => {
                        const blockQuestions = groupedQuestions[block] ?? [];

                        return (
                            <FlexCol key={block} className="gap-[10px]">
                                <span className="text-[#637381] text-lg font-medium leading-6">
                                    {block}
                                </span>

                                <FlexCol className="gap-5">
                                    {blockQuestions.map((question) => {
                                        const answer = answers[question.id] ?? null;

                                        return (
                                            <div
                                                key={question.id}
                                                className="bg-white border border-[#DFE4EA] rounded-lg p-5 flex flex-col gap-5 w-full"
                                            >
                                                <FlexCol className="gap-[10px]">
                                                    <div className="markdown-text  text-[#111928] text-lg font-medium leading-6 w-full">                  
                                                        <ReactMarkdown  remarkPlugins={[remarkBreaks, remarkGfm]}
                                                          rehypePlugins={[rehypeSanitize]}>
                                                            {question.Title}
                                                        </ReactMarkdown>
                                                    </div>
                                                    {question.Description && (
                                                        <div className="markdown-text text-[#637381] text-sm font-normal leading-[18px] w-full">
                                                            <ReactMarkdown  remarkPlugins={[remarkBreaks, remarkGfm]}
                                                              rehypePlugins={[rehypeSanitize]}>
                                                            {question.Description}
                                                        </ReactMarkdown>
                                                        </div>
                                                    )}
                                                </FlexCol>

                                                <FlexRow className="gap-[10px] items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAnswerChange(question.id, "yes")}
                                                        className={twMerge(
                                                            "flex gap-[10px] items-center shrink-0",
                                                            viewMode && "cursor-default",
                                                        )}
                                                        aria-pressed={answer === "yes"}
                                                        disabled={viewMode}
                                                    >
                                                        <RadioCircle checked={answer === "yes"} />
                                                        <span className="text-[#111928] text-base font-normal leading-[22px]">
                                                            Да
                                                        </span>
                                                    </button>
                                                    
                                                </FlexRow>

                                                <FlexRow className="gap-[10px] items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAnswerChange(question.id, "no")}
                                                        className={twMerge(
                                                            "flex gap-[10px] items-center shrink-0",
                                                            viewMode && "cursor-default",
                                                        )}
                                                        aria-pressed={answer === "no"}
                                                        disabled={viewMode}
                                                    >
                                                        <RadioCircle checked={answer === "no"} />
                                                        <span className="text-[#111928] text-base font-normal leading-[22px]">
                                                            Нет
                                                        </span>
                                                    </button>
                                                </FlexRow>
                                            </div>
                                        );
                                    })}
                                </FlexCol>
                            </FlexCol>
                        );
                    })}

                    <FlexRow className="items-center gap-[10px]">
                        <span className="text-[#111928] text-2xl font-semibold leading-[30px]">Итого:</span>
                        <span className={twMerge("text-2xl font-semibold leading-[30px]", getRatingTextColor(totalScore, colorThresholds))}>
                            {formatWeight(totalScore)}
                        </span>
                    </FlexRow>

                    <Comments
                        history={false}
                        zIndex={zIndex}
                        IDCard={card5WayID}
                        listComments={myTasks5WayCommentsListName}
                        listFilesComments={myTask5WayCommentsFilesListName}
                        strapiParams={MT5WhyCommentsFiles}
                        field="_x0035_Way"
                        customClass="w-full"
                        variant="inlineAdd"
                        label="Комментарии и вложения"
                        isRatingComment={true}
                    />
                </FlexCol>
            </div>

            <FlexRow className="items-end justify-between px-10 pb-10 mt-auto">
                <Button classBg="btn-white" shadow={true} text="Назад" customFunction={onClose} />
                {!viewMode && (
                    <Button
                        classBg="btn-dark"
                        text={submitButtonText}
                        customFunction={handleSubmit}
                        disabled={submitting}
                    />
                )}
            </FlexRow>
        </FlexCol>
    );
};

type RadioCircleProps = {
    checked: boolean;
};

const RadioCircle = ({ checked }: RadioCircleProps) => (
    <span
        className={twMerge(
            "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
            checked ? "border-[#012169] bg-white" : "border-[#DFE4EA] bg-white",
        )}
        aria-hidden="true"
    >
        {checked && <span className="w-[10px] h-[10px] rounded-full bg-[#012169]" />}
    </span>
);

export default Rate5Why;
